import { syntaxTree } from "@codemirror/language";
import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin, type DecorationSet, type EditorView, type ViewUpdate } from "@codemirror/view";

const OPEN = new Set(["(", "[", "{"]);
const CLOSE = new Set([")", "]", "}"]);
const LEVELS = [0, 1, 2].map((level) => Decoration.mark({ class: `wb-bracket-${level}` }));

/**
 * Bracket pair colorization, on by default in VS Code: brackets cycle through
 * three colors by nesting depth. Brackets are the syntax tree's bracket
 * tokens, so those in strings and comments are left alone. The depth at the
 * viewport is counted from the start of the document.
 */
export const bracketColors = ViewPlugin.fromClass(
    class {
        decorations: DecorationSet;
        constructor(view: EditorView) {
            this.decorations = colorize(view);
        }
        update(update: ViewUpdate) {
            if (
                update.docChanged ||
                update.viewportChanged ||
                syntaxTree(update.state) !== syntaxTree(update.startState)
            ) {
                this.decorations = colorize(update.view);
            }
        }
    },
    { decorations: (plugin) => plugin.decorations },
);

function colorize(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const { from, to } = view.viewport;
    let depth = 0;
    syntaxTree(view.state).iterate({
        to,
        enter: (node) => {
            if (node.to - node.from !== 1) return;
            const name = node.name;
            if (OPEN.has(name)) {
                if (node.from >= from) builder.add(node.from, node.to, LEVELS[depth % 3]!);
                depth++;
            } else if (CLOSE.has(name)) {
                depth = Math.max(0, depth - 1);
                if (node.from >= from) builder.add(node.from, node.to, LEVELS[depth % 3]!);
            }
        },
    });
    return builder.finish();
}
